import React, { useEffect, useState } from 'react';
import { Laboratorio, LaboratorioDisciplina, ModoLaboratorio, laboratorioDisciplinaService } from '../services/api';

interface LaboratorioDisciplinaModalProps {
  isOpen: boolean;
  onClose: () => void;
  disciplina: { id: string; nome: string; cargaHoraria: number };
  laboratorios: Laboratorio[];
  onSucesso: () => void;
}

const LaboratorioDisciplinaModal: React.FC<LaboratorioDisciplinaModalProps> = ({
  isOpen,
  onClose,
  disciplina,
  laboratorios,
  onSucesso,
}) => {
  const [vinculoExistente, setVinculoExistente] = useState<LaboratorioDisciplina | null>(null);
  const [modo, setModo] = useState<ModoLaboratorio>('PREFERENCIAL');
  const [laboratorioId, setLaboratorioId] = useState('');
  const [quantidade, setQuantidade] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmandoRemocao, setConfirmandoRemocao] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setConfirmandoRemocao(false);
    setLoading(true);
    laboratorioDisciplinaService
      .getByDisciplina(disciplina.id)
      .then((existente) => {
        setVinculoExistente(existente);
        if (existente) {
          setModo(existente.modo);
          setLaboratorioId(existente.laboratorioId ?? '');
          setQuantidade(existente.quantidade);
        } else {
          setModo('PREFERENCIAL');
          setLaboratorioId(laboratorios[0]?.id ?? '');
          setQuantidade(1);
        }
      })
      .catch(() => setError('Erro ao carregar configuração de laboratório desta disciplina.'))
      .finally(() => setLoading(false));
  }, [isOpen, disciplina.id, laboratorios]);

  if (!isOpen) return null;

  const precisaDeLaboratorio = modo !== 'QUALQUER';
  const opcoesQuantidade = Array.from({ length: disciplina.cargaHoraria }, (_, i) => i + 1);

  const handleSalvar = async () => {
    if (precisaDeLaboratorio && !laboratorioId) {
      setError('Selecione um laboratório para o modo preferencial ou exclusivo.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const request = {
        disciplinaId: disciplina.id,
        quantidade,
        modo,
        laboratorioId: precisaDeLaboratorio ? laboratorioId : undefined,
      };
      if (vinculoExistente) {
        await laboratorioDisciplinaService.update(vinculoExistente.id, request);
      } else {
        await laboratorioDisciplinaService.create(request);
      }
      onSucesso();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao salvar a configuração de laboratório.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemover = async () => {
    if (!vinculoExistente) return;
    setSaving(true);
    setError(null);
    try {
      await laboratorioDisciplinaService.delete(vinculoExistente.id);
      onSucesso();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao remover o vínculo de laboratório.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fas fa-flask me-2 text-primary"></i>
              Aulas em Laboratório — {disciplina.nome}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={saving}></button>
          </div>

          <div className="modal-body">
            {loading ? (
              <div className="text-center py-3">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Carregando...</span>
                </div>
              </div>
            ) : laboratorios.length === 0 ? (
              <div className="alert alert-warning mb-0">
                <i className="fas fa-exclamation-triangle me-2"></i>
                Nenhum laboratório cadastrado ainda. Cadastre um em <strong>Laboratórios</strong> antes de vincular esta disciplina.
              </div>
            ) : (
              <>
                {error && <div className="alert alert-danger">{error}</div>}

                <div className="mb-3">
                  <label className="form-label fw-semibold">Quantas das {disciplina.cargaHoraria} aula(s) semanais são em laboratório?</label>
                  <select
                    className="form-select"
                    value={quantidade}
                    onChange={(e) => setQuantidade(Number(e.target.value))}
                    disabled={saving}
                  >
                    {opcoesQuantidade.map((n) => (
                      <option key={n} value={n}>{n} aula{n > 1 ? 's' : ''}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Qual laboratório?</label>
                  <div className="d-flex flex-column gap-2">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="modoLaboratorio"
                        id="modo-qualquer"
                        checked={modo === 'QUALQUER'}
                        onChange={() => setModo('QUALQUER')}
                        disabled={saving}
                      />
                      <label className="form-check-label" htmlFor="modo-qualquer">
                        <strong>Qualquer um</strong> — usa o que estiver livre no horário
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="modoLaboratorio"
                        id="modo-preferencial"
                        checked={modo === 'PREFERENCIAL'}
                        onChange={() => setModo('PREFERENCIAL')}
                        disabled={saving}
                      />
                      <label className="form-check-label" htmlFor="modo-preferencial">
                        <strong>Preferencial</strong> — tenta usar o escolhido, mas aceita outro se precisar
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="modoLaboratorio"
                        id="modo-exclusivo"
                        checked={modo === 'EXCLUSIVO'}
                        onChange={() => setModo('EXCLUSIVO')}
                        disabled={saving}
                      />
                      <label className="form-check-label" htmlFor="modo-exclusivo">
                        <strong>Exclusivamente este</strong> — só esse laboratório; se não der, a geração avisa
                      </label>
                    </div>
                  </div>
                </div>

                {precisaDeLaboratorio && (
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Laboratório</label>
                    <select
                      className="form-select"
                      value={laboratorioId}
                      onChange={(e) => setLaboratorioId(e.target.value)}
                      disabled={saving}
                    >
                      <option value="">Selecione...</option>
                      {laboratorios.map((lab) => (
                        <option key={lab.id} value={lab.id}>{lab.nome}</option>
                      ))}
                    </select>
                  </div>
                )}

                {vinculoExistente && (
                  <div className="border-top pt-3 mt-3">
                    {!confirmandoRemocao ? (
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => setConfirmandoRemocao(true)}
                        disabled={saving}
                      >
                        <i className="fas fa-trash me-2"></i>
                        Remover aulas em laboratório desta disciplina
                      </button>
                    ) : (
                      <div className="alert alert-danger mb-0">
                        <p className="mb-2">Tem certeza? A disciplina deixa de ter aulas em laboratório.</p>
                        <div className="d-flex gap-2">
                          <button type="button" className="btn btn-danger btn-sm" onClick={handleRemover} disabled={saving}>
                            Sim, remover
                          </button>
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setConfirmandoRemocao(false)} disabled={saving}>
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSalvar}
              disabled={saving || loading || laboratorios.length === 0}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Salvando...
                </>
              ) : (
                <>
                  <i className="fas fa-save me-2"></i>
                  Salvar
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LaboratorioDisciplinaModal;
